import React, { useEffect, useState, Component } from 'react';
/**
 * Boot Sequence Component
 * Simulates OS initialization with code generation visualization
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Terminal, Code, Zap, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
interface BootSequenceProps {
  onComplete: () => void;
}
type BootStage = 'init' | 'building' | 'menu' | 'apps' | 'finalizing' | 'complete';
export function BootSequence({
  onComplete
}: BootSequenceProps) {
  const {
    theme: currentTheme
  } = useTheme();
  const [stage, setStage] = useState<BootStage>('init');
  const [progress, setProgress] = useState(0);
  const [codeLines, setCodeLines] = useState<string[]>([]);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const buildSteps = [{
    stage: 'init',
    duration: 500,
    message: 'Initializing MVL Creative OS...'
  }, {
    stage: 'building',
    duration: 1000,
    message: 'Building core systems...'
  }, {
    stage: 'menu',
    duration: 700,
    message: 'Generating menu system...'
  }, {
    stage: 'apps',
    duration: 800,
    message: 'Loading applications...'
  }, {
    stage: 'finalizing',
    duration: 500,
    message: 'Finalizing setup...'
  }, {
    stage: 'complete',
    duration: 300,
    message: 'Ready!'
  }];
  const codeSnippets = ['import { ThemeProvider } from "./contexts/ThemeContext"', 'import { WindowProvider } from "./contexts/WindowContext"', 'import { AppRegistryProvider } from "./contexts/AppRegistry"', 'const apps = await loadApplications()', 'registerApp({ id: "home", title: "Home", ... })', 'registerApp({ id: "studio", title: "Studio", ... })', 'registerApp({ id: "projects", title: "Projects", ... })', 'registerApp({ id: "expertise", title: "Expertise", ... })', 'registerApp({ id: "contact", title: "Contact", ... })', 'initializeWindowManager()', 'setupDockSystem()', 'applyThemePreferences()', 'mountDesktopEnvironment()', '✓ System ready'];
  useEffect(() => {
    let currentStepIndex = 0;
    let currentProgress = 0;
    let lineIndex = 0;
    const progressPerStep = 100 / buildSteps.length;
    const advanceStage = () => {
      if (currentStepIndex >= buildSteps.length) {
        setTimeout(onComplete, 200);
        return;
      }
      const step = buildSteps[currentStepIndex];
      setStage(step.stage as BootStage);
      const progressInterval = setInterval(() => {
        currentProgress += 3;
        setProgress(Math.min(currentProgress, (currentStepIndex + 1) * progressPerStep));
      }, step.duration / 30);
      if (step.stage === 'building' || step.stage === 'menu' || step.stage === 'apps') {
        const linesPerStage = 4;
        const lineInterval = setInterval(() => {
          if (lineIndex < codeSnippets.length) {
            setCodeLines(prev => [...prev, codeSnippets[lineIndex]]);
            lineIndex++;
          }
        }, step.duration / linesPerStage);
        setTimeout(() => clearInterval(lineInterval), step.duration);
      }
      setTimeout(() => {
        clearInterval(progressInterval);
        currentStepIndex++;
        advanceStage();
      }, step.duration);
    };
    advanceStage();
  }, []);
  const currentMessage = buildSteps.find(s => s.stage === stage)?.message || '';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className={`fixed inset-0 z-[10000] flex items-center justify-center ${isLightTheme ? 'bg-gradient-to-br from-white to-gray-100' : isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-black' : 'bg-gradient-to-br from-gray-900 to-black'}`} data-id="element-835">
      {/* Ambient particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-836">
        {[...Array(15)].map((_, i) => <motion.div key={`particle-${i}`} className={`absolute w-1 h-1 rounded-full ${isNeonTheme ? 'bg-cyan-400' : 'bg-fuchsia-400'}`} style={{
        left: `${i * 7 % 100}%`,
        top: `${i * 11 % 100}%`
      }} animate={{
        y: [-20, -50, -20],
        opacity: [0.3, 0.7, 0.3],
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 4 + i % 3,
        repeat: Infinity,
        delay: i * 0.2,
        ease: 'easeInOut'
      }} data-id="element-837" />)}
      </div>
      {/* Main content */}
      <div className="relative z-10 w-full max-w-2xl px-6" data-id="element-838">
        {/* MVL Creative Logo */}
        <motion.div initial={{
        scale: 0,
        rotate: -180
      }} animate={{
        scale: 1,
        rotate: 0
      }} transition={{
        type: 'spring',
        duration: 0.8,
        bounce: 0.4
      }} className="flex justify-center mb-8" data-id="element-839">
          <div className="relative" data-id="element-840">
            {/* Glow effect */}
            {!isHighContrast && <motion.div animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className={`absolute inset-0 rounded-2xl blur-2xl ${isNeonTheme ? 'bg-gradient-to-br from-cyan-500 to-fuchsia-500' : 'bg-gradient-to-br from-fuchsia-500 to-violet-500'}`} data-id="element-841" />}
            {/* Logo container with tech border */}
            <div className={`relative w-24 h-24 rounded-2xl overflow-hidden bg-white ${isHighContrast ? 'border-4 border-white' : isNeonTheme ? 'border-2 border-cyan-500/50' : 'border-2 border-fuchsia-500/30'}`} data-id="element-842">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rGwPzEqbTtWGmo9zdFVp6V/Gemini_Generated_Image_z1lwq1z1lwq1z1lw-removebg-preview.png" alt="MVL Creative" className="w-full h-full object-contain p-3" data-id="element-843" />
            </div>
            {/* Rotating tech ring */}
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className={`absolute inset-0 rounded-2xl border-2 border-dashed ${isNeonTheme ? 'border-cyan-400/40' : 'border-fuchsia-400/40'}`} data-id="element-844" />
            {/* Corner accents */}
            {!isHighContrast && <>
                <div className={`absolute -top-1 -left-1 w-3 h-3 ${isNeonTheme ? 'bg-cyan-400' : 'bg-fuchsia-400'}`} style={{
              clipPath: 'polygon(0 0, 100% 0, 0 100%)'
            }} data-id="element-845" />
                <div className={`absolute -top-1 -right-1 w-3 h-3 ${isNeonTheme ? 'bg-fuchsia-400' : 'bg-violet-400'}`} style={{
              clipPath: 'polygon(100% 0, 100% 100%, 0 0)'
            }} data-id="element-846" />
              </>}
          </div>
        </motion.div>
        {/* Status message */}
        <motion.div key={stage} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="text-center mb-8" data-id="element-847">
          <h2 className={`text-2xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-white' : 'text-white'}`} data-id="element-848">
            {currentMessage}
          </h2>
          <div className={`flex items-center justify-center gap-2 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-849">
            {stage === 'complete' ? <>
                <CheckCircle2 className="w-4 h-4" data-id="element-850" />
                <span className="text-sm" data-id="element-851">System initialized</span>
              </> : <>
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-852">
                  <Terminal className="w-4 h-4" data-id="element-853" />
                </motion.div>
                <span className="text-sm" data-id="element-854">{Math.round(progress)}%</span>
              </>}
          </div>
        </motion.div>
        {/* Code terminal */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.3
      }} className={`rounded-2xl border overflow-hidden ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-gray-900 border-white' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-black/50 border-white/10'}`} data-id="element-855">
          {/* Terminal header */}
          <div className={`flex items-center gap-2 px-4 py-3 border-b ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-gray-800 border-white' : isNeonTheme ? 'bg-black border-fuchsia-500/30' : 'bg-black/30 border-white/10'}`} data-id="element-856">
            <div className="flex gap-2" data-id="element-857">
              <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-858" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-859" />
              <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-860" />
            </div>
            <div className={`text-xs font-mono ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-861">
              system.build.log
            </div>
          </div>
          {/* Terminal content */}
          <div className="p-4 font-mono text-xs h-48 overflow-hidden" data-id="element-862">
            <AnimatePresence mode="popLayout" data-id="element-863">
              {codeLines.map((line, i) => <motion.div key={`line-${i}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.15
            }} className={`mb-1 flex items-center gap-2 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-200' : isNeonTheme ? 'text-cyan-300' : 'text-emerald-400'}`} data-id="element-864">
                  {line.startsWith('✓') ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-865" /> : <Code className="w-3 h-3 opacity-50 flex-shrink-0" data-id="element-866" />}
                  <span className="truncate" data-id="element-867">{line}</span>
                  {i === codeLines.length - 1 && !line.startsWith('✓') && <motion.span animate={{
                opacity: [1, 0]
              }} transition={{
                duration: 0.5,
                repeat: Infinity
              }} className="ml-1" data-id="element-868">
                      _
                    </motion.span>}
                </motion.div>)}
            </AnimatePresence>
          </div>
        </motion.div>
        {/* Progress bar */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="mt-6" data-id="element-869">
          <div className={`h-2 rounded-full overflow-hidden ${isLightTheme ? 'bg-gray-200' : isHighContrast ? 'bg-gray-800' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/10'}`} data-id="element-870">
            <motion.div className={`h-full ${isHighContrast ? 'bg-white' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500'}`} initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.2
          }} data-id="element-871" />
          </div>
        </motion.div>
      </div>
    </motion.div>;
}